import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Button,
  HStack,
  Box,
  Text,
  Heading,
  useColorModeValue,
  Flex,
  IconButton,
  Divider,
  Badge,
  Select,
  Grid,
  GridItem,
  Collapse,
  useDisclosure,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  useBreakpointValue,
  Alert,
  AlertIcon,
  AlertDescription,
  Checkbox,
  Portal,
  Modal,
  Accordion, // Added for CitationManager
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea as ChakraTextarea, // Added to fix ReferenceError, aliased for debugging
} from '@chakra-ui/react';
import {
  Clock,
  PlusCircle,
  Trash,
  ArrowUp,
  ArrowDown,
  DotsThreeVertical,
  WarningCircle,
  Lightning,
  CheckCircle,
  Star,
  PencilSimple,
  Calendar,
  PushPinSimple,
  Notebook, // Added for CitationManager
} from '@phosphor-icons/react';
import { useCreateSite } from '../../../context/CreateSiteContext';
import { motion, AnimatePresence } from 'framer-motion';
import VisualTimeline from '../utils/VisualTimeline';
import CitationManager from '../utils/CitationManager'; // Import CitationManager
import SourceLinkedText from '../../site/SourceLinkedText'; // Import SourceLinkedText
import { renumberInTextCitations } from '../../../utils/citationUtils'; // Import renumberInTextCitations

const MotionBox = motion(Box);

// Helper Components
const PeriodItem = ({
  period,
  index,
  isMainPeriod,
  onDelete,
  onMoveUp,
  onMoveDown,
  onEdit,
  onSetAsMain,
  isFirst,
  isLast,
  siteData, // Pass siteData for citations and groundingMetadata
}) => {
  const bgColor = useColorModeValue(
    isMainPeriod ? 'rgba(193, 148, 52, 0.1)' : 'bg.surface', // Use surface bg for non-main
    isMainPeriod ? 'rgba(214, 173, 96, 0.1)' : 'gray.750' // Slightly different dark bg
  );
  const borderColor = useColorModeValue(
    isMainPeriod ? 'rgba(193, 148, 52, 0.3)' : 'border.subtle',
    isMainPeriod ? 'rgba(214, 173, 96, 0.3)' : 'border.subtle'
  );
  const accentColor = useColorModeValue('brand.primary', 'brand.primary');
  const mutedColor = useColorModeValue('text.muted', 'text.muted');
  const textColor = useColorModeValue('text.default', 'text.default');
  const iconSize = useBreakpointValue({ base: 14, md: 16 });

  return (
    <MotionBox
      p={4}
      borderWidth="1px"
      borderRadius="lg" // Slightly larger radius
      borderColor={borderColor}
      bg={bgColor}
      mb={3}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
      transition={{ duration: 0.3 }}
      position="relative"
      // Enhanced main period indicator
      _before={isMainPeriod ? {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '5px', // Thicker line
        height: '100%',
        bg: accentColor,
        borderTopLeftRadius: 'lg',
        borderBottomLeftRadius: 'lg',
        boxShadow: `1px 0 5px ${accentColor}33` // Subtle glow
      } : undefined}
    >
      <Flex direction={{ base: 'column', sm: 'row' }} justify="space-between" align={{ base: 'stretch', sm: 'center' }}>
        {/* Left Side: Period Info */}
        <VStack align="start" spacing={2} flex={1} mb={{ base: 3, sm: 0 }} mr={{ base: 0, sm: 4 }}>
          <HStack spacing={3} align="center">
            <Heading size="sm" fontFamily="heading" color={textColor}>
              {period.periodName || "Unnamed Period"}
            </Heading>
            {isMainPeriod && (
              <Badge
                colorScheme="yellow"
                variant="solid"
                fontSize="xs"
                px={2}
                py={0.5}
                borderRadius="full"
              >
                <HStack spacing={1}>
                  <Star size={10} weight="fill" />
                  <Text>Main</Text>
                </HStack>
              </Badge>
            )}
          </HStack>

          <HStack spacing={4} color={mutedColor} fontSize="sm" flexWrap="wrap">
            <HStack>
              <Calendar size={iconSize} />
              <Text>{period.startEra || '?'} to {period.endEra || '?'}</Text>
            </HStack>
            {period.culture && (
              <Text fontStyle="italic" noOfLines={1}>
                {period.culture}
              </Text>
            )}
          </HStack>

          {period.features && period.features.length > 0 && (
            <Box mt={1}>
              <Text fontSize="xs" color={mutedColor} mb={1}>Features:</Text>
              <Flex wrap="wrap" gap={1.5}> {/* Use Flex with gap for better wrapping */}
                {period.features.map((feature, idx) => (
                  <Badge
                    key={idx}
                    colorScheme="yellow"
                    variant="subtle"
                    fontSize="xs"
                    px={1.5} // Add padding
                    borderRadius="sm" // Slightly rounded
                  >
                    {feature}
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}

          {/* Display Period Description */}
          {period.details && (
            <Box mt={2} pl={1} fontSize="sm" color={textColor} whiteSpace="pre-wrap">
              <SourceLinkedText
                text={period.details}
                citations={siteData.citations || []}
                groundingMetadata={
                  siteData.historicalContextAndChronology_narrative?.groundingMetadata ||
                  siteData.groundingMetadata || 
                  { groundingSupports: [] }
                }
              />
            </Box>
          )}
        </VStack>

        {/* Right Side: Actions */}
        <Flex align="center" justify={{ base: 'flex-end', sm: 'center' }} mt={{ base: 2, sm: 0 }}>
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              icon={<DotsThreeVertical size={iconSize + 2} />} // Slightly larger icon
              variant="ghost"
              size="sm"
              aria-label="More options"
              color={mutedColor}
              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
            />
            <MenuList minW="160px"> {/* Ensure enough width */}
              <MenuItem
                icon={<PencilSimple size={iconSize} />}
                onClick={() => onEdit(index)}
              >
                Edit Period
              </MenuItem>
              {!isMainPeriod && (
                <MenuItem
                  icon={<Star size={iconSize} />}
                  onClick={() => onSetAsMain(index)}
                >
                  Set as Main
                </MenuItem>
              )}
              <MenuItem
                icon={<ArrowUp size={iconSize} />}
                isDisabled={isFirst}
                onClick={() => onMoveUp(index)}
              >
                Move Up
              </MenuItem>
              <MenuItem
                icon={<ArrowDown size={iconSize} />}
                isDisabled={isLast}
                onClick={() => onMoveDown(index)}
              >
                Move Down
              </MenuItem>
              <MenuItem
                icon={<Trash size={iconSize} />}
                color="red.500"
                onClick={() => onDelete(index)}
              >
                Delete Period
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>
    </MotionBox>
  );
};


// Helper to parse era string like "3000 BCE" or "3000 BCE?" into { year: 3000, designation: 'BCE', isPresent: false, isApproximate: true/false }
const parseEraString = (eraStringInput) => { // Make sure this function is defined before PeriodForm if it's used by it.
  if (!eraStringInput) return { year: '', designation: 'CE', isPresent: false, isApproximate: false };

  let eraString = eraStringInput;
  const isApproximate = eraString.endsWith('?');
  if (isApproximate) {
    eraString = eraString.slice(0, -1); // Remove the '?'
  }

  if (eraString.toLowerCase() === 'present') {
    return { year: '', designation: 'CE', isPresent: true, isApproximate }; // Approximate doesn't really apply to "present" but store it if passed
  }
  
  const match = eraString.match(/^(\d+)\s+(BCE|CE)$/i);
  if (match) {
    return { year: parseInt(match[1], 10), designation: match[2].toUpperCase(), isPresent: false, isApproximate };
  }
  // Fallback for potentially just a number (assume CE) or invalid format
  const yearMatch = eraString.match(/^\d+$/);
  return { year: yearMatch ? parseInt(eraString, 10) : '', designation: 'CE', isPresent: false, isApproximate };
};

// Helper to format year and designation into "YYYY BCE/CE" string, optionally adding "?"
const formatEraString = (year, designation, isApproximate) => {
  if (year === '' || year === null || year === undefined) return '';
  const baseString = `${year} ${designation}`;
  return isApproximate ? `${baseString}?` : baseString;
};


const PeriodForm = ({ period, onSave, onCancel, isEditing = false, onDetailsFocus, periodIndex, detailsInputRef, siteData }) => {
  // Parse existing period data on initial load or when period changes
  const getInitialFormValues = (p) => {
    const initialStartEra = parseEraString(p?.startEra);
    const initialEndEra = parseEraString(p?.endEra);
    return {
      periodName: p?.periodName || '',
      startYear: initialStartEra.year,
      startDesignation: initialStartEra.designation,
      startEraApproximate: p?.startEraApproximate ?? initialStartEra.isApproximate, // Prioritize explicit flag
      endYear: initialEndEra.year,
      endDesignation: initialEndEra.designation,
      endEraApproximate: p?.endEraApproximate ?? initialEndEra.isApproximate, // Prioritize explicit flag
      isPresent: initialEndEra.isPresent || false,
      culture: p?.culture || '',
      features: p?.features?.join(', ') || '',
      details: p?.details || '' // Added details field
    };
  };

  const [formValues, setFormValues] = useState(getInitialFormValues(period));

  // Update state if the period prop changes (e.g., when switching between edit/add)
  useEffect(() => {
    setFormValues(getInitialFormValues(period));
  }, [period]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormValues(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'startYear' || name === 'endYear') {
      const numericValue = value === '' ? '' : parseInt(value, 10);
      setFormValues(prev => ({
        ...prev,
        [name]: isNaN(numericValue) ? '' : numericValue
      }));
    } else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const features = formValues.features.split(',').map(item => item.trim()).filter(Boolean);

    const startEraString = formatEraString(formValues.startYear, formValues.startDesignation, formValues.startEraApproximate);
    const endEraString = formValues.isPresent ? 'present' : formatEraString(formValues.endYear, formValues.endDesignation, formValues.endEraApproximate);

    onSave({
      periodName: formValues.periodName,
      startEra: startEraString,
      endEra: endEraString,
      startEraApproximate: formValues.startEraApproximate,
      endEraApproximate: formValues.isPresent ? false : formValues.endEraApproximate, // if present, not approximate
      culture: formValues.culture,
      features,
      details: formValues.details, // Save details
      // Preserve isMain if it exists on the original period object (important for edits)
      isMain: period?.isMain !== undefined ? period.isMain : (isEditing ? false : undefined)
    });
  };
  
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  // Removed internal detailsTextareaRef, will use detailsInputRef prop directly
  
  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      p={4}
      borderWidth="1px"
      borderRadius="md"
      borderStyle="dashed"
      borderColor={borderColor}
      bg={bgColor}
      mb={4}
    >
      <VStack spacing={4}>
        <Grid templateColumns="repeat(12, 1fr)" gap={4} width="100%">
          <GridItem colSpan={12}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Period Name</FormLabel>
              <Input
                name="periodName"
                value={formValues.periodName}
                onChange={handleChange}
                placeholder="e.g. Neolithic Construction"
                size="sm"
              />
            </FormControl>
          </GridItem>
          
          <GridItem colSpan={{ base: 12, sm: 6 }}>
            <FormControl>
              <FormLabel fontSize="sm">Start Era</FormLabel>
              <HStack>
                <Input
                  name="startYear"
                  type="number"
                  value={formValues.startYear}
                  onChange={handleChange}
                  placeholder="Year"
                  size="sm"
                  flex={2}
                />
                <Select
                  name="startDesignation"
                  value={formValues.startDesignation}
                  onChange={handleChange}
                  size="sm"
                  flex={1}
                >
                  <option value="BCE">BCE</option>
                  <option value="CE">CE</option>
                </Select>
              </HStack>
              <Checkbox name="startEraApproximate" isChecked={formValues.startEraApproximate} onChange={handleChange} size="sm" mt={2}>
                Approximate Start Era?
              </Checkbox>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, sm: 6 }}>
            <FormControl>
              <FormLabel fontSize="sm">End Era</FormLabel>
              <HStack>
                <Input
                  name="endYear"
                  type="number"
                  value={formValues.endYear}
                  onChange={handleChange}
                  placeholder="Year"
                  size="sm"
                  flex={2}
                  isDisabled={formValues.isPresent}
                />
                <Select
                  name="endDesignation"
                  value={formValues.endDesignation}
                  onChange={handleChange}
                  size="sm"
                  flex={1}
                  isDisabled={formValues.isPresent}
                >
                  <option value="BCE">BCE</option>
                  <option value="CE">CE</option>
                </Select>
              </HStack>
              <HStack mt={2} spacing={4}>
                <Checkbox name="isPresent" isChecked={formValues.isPresent} onChange={handleChange} size="sm">
                  Set to Present Day
                </Checkbox>
                {!formValues.isPresent && (
                  <Checkbox name="endEraApproximate" isChecked={formValues.endEraApproximate} onChange={handleChange} size="sm">
                    Approximate End Era?
                  </Checkbox>
                )}
              </HStack>
            </FormControl>
          </GridItem>
          
          <GridItem colSpan={{ base: 12, sm: 6 }}>
            <FormControl>
              <FormLabel fontSize="sm">Culture</FormLabel>
              <Input
                name="culture"
                value={formValues.culture}
                onChange={handleChange}
                placeholder="e.g. Celtic, Neolithic"
                size="sm"
              />
            </FormControl>
          </GridItem>
          
          <GridItem colSpan={{ base: 12, sm: 6 }}>
            <FormControl>
              <FormLabel fontSize="sm">Features</FormLabel>
              <Input
                name="features"
                value={formValues.features}
                onChange={handleChange}
                placeholder="e.g. Stone Circle, Altar, Mound"
                size="sm"
              />
              <FormHelperText fontSize="xs">Separate with commas</FormHelperText>
            </FormControl>
          </GridItem>
          <GridItem colSpan={12}>
            <FormControl>
              <FormLabel fontSize="sm">Details / Description for this Period</FormLabel>
              <ChakraTextarea
                name="details"
                value={formValues.details}
                onChange={handleChange}
                placeholder="e.g. Main construction phase, site abandonment. You can insert [citations] here."
                size="sm"
                minHeight="80px" // Give it some initial height
                onFocus={() => onDetailsFocus && onDetailsFocus(periodIndex)} // Call onDetailsFocus
                ref={detailsInputRef} // Assign the ref passed via props
              />
              {/* Preview for PeriodForm details */}
              <Box mt={2} p={2} borderWidth="1px" borderRadius="sm" borderColor={useColorModeValue('gray.200', 'gray.600')} bg={useColorModeValue("gray.50", "gray.700")} fontSize="sm">
                <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} mb={1}>Preview:</Text>
                <SourceLinkedText
                  text={formValues.details}
                  citations={siteData.citations || []}
                  groundingMetadata={
                    siteData.historicalContextAndChronology_narrative?.groundingMetadata ||
                    siteData.groundingMetadata ||
                    { groundingSupports: [] }
                  }
                />
              </Box>
            </FormControl>
          </GridItem>
        </Grid>
        
        <HStack width="100%" justify="flex-end">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" type="submit" colorScheme="yellow">
            {isEditing ? 'Update Period' : 'Add Period'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export const TimelineStep = () => {
  const {
    siteData,
    setSiteData,
    validationErrors,
    citations, // Get citations from context
    // addCitation, updateCitation, deleteCitation are available if needed directly,
    // but CitationManager will use them from its own context access.
  } = useCreateSite();
  
  const { isOpen, onOpen, onClose } = useDisclosure(); // Modal state for PeriodForm
  const [editingPeriodIndex, setEditingPeriodIndex] = useState(null); // null for adding, index for editing
  const [showTimelineHelp, setShowTimelineHelp] = useState(!siteData.constructionPeriods?.length);
  const toast = useToast();
  const [activePeriodIndexForCitation, setActivePeriodIndexForCitation] = useState(null);
  const [citationManagerAccordionIndex, setCitationManagerAccordionIndex] = useState(-1); // -1 for closed, 0 for open
  const periodDetailsTextareaRefs = useRef({}); // To store refs for period detail textareas


  const periodList = siteData.constructionPeriods || [];
  const mainPeriodIndex = siteData.mainPeriodIndex ?? 0;
  
  // Color mode values
  const mutedColor = useColorModeValue('text.muted', 'text.muted');
  const cardBg = useColorModeValue('bg.surface', 'bg.surface');
  const accentColor = useColorModeValue('brand.primary', 'brand.primary');
  
  // Effect to update the estimatedEra field based on the main period's era
  useEffect(() => {
    // Only proceed if we have construction periods and a valid main period index
    if (periodList.length > 0 && mainPeriodIndex !== null && mainPeriodIndex < periodList.length) {
      const mainPeriod = periodList[mainPeriodIndex];
      
      // Only update if the main period has at least one of the era fields
      if (mainPeriod.startEra || mainPeriod.endEra) {
        let estimatedEraText = '';
        
        // Construct a string that includes available era information
        if (mainPeriod.startEra && mainPeriod.endEra) {
          estimatedEraText = `${mainPeriod.startEra} - ${mainPeriod.endEra}`;
        } else if (mainPeriod.startEra) {
          estimatedEraText = `${mainPeriod.startEra} onwards`;
        } else if (mainPeriod.endEra) {
          estimatedEraText = `Until ${mainPeriod.endEra}`;
        }
        
        // Add culture information if available
        if (mainPeriod.culture) {
          estimatedEraText += estimatedEraText ? ` (${mainPeriod.culture})` : mainPeriod.culture;
        }
        
        // Update the estimatedEra field in siteData if it's different
        if (estimatedEraText && estimatedEraText !== siteData.estimatedEra) {
          setSiteData({
            ...siteData,
            estimatedEra: estimatedEraText
          });
        }
      }
    }
  }, [periodList, mainPeriodIndex, siteData, setSiteData]);
  
  // Handlers for period operations
  const handleAddPeriod = (newPeriod) => {
    const updatedPeriods = [...periodList, newPeriod];
    setSiteData({
      ...siteData,
      constructionPeriods: updatedPeriods,
      // If this is the first period, set it as main
      mainPeriodIndex: periodList.length === 0 ? 0 : siteData.mainPeriodIndex,
    });
    onClose(); // Close modal after adding
    toast({
      title: 'Period added',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };
  
  const handleUpdatePeriod = (updatedPeriod) => {
    if (editingPeriodIndex === null) return;
    
    const updatedPeriods = [...periodList];
    updatedPeriods[editingPeriodIndex] = updatedPeriod;
    
    setSiteData({
      ...siteData,
      constructionPeriods: updatedPeriods,
    });

    onClose(); // Close modal after updating
    toast({
      title: 'Period updated',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };
  
  const handleDeletePeriod = (index) => {
    const updatedPeriods = periodList.filter((_, i) => i !== index);
    
    // Adjust mainPeriodIndex if needed
    let newMainIndex = siteData.mainPeriodIndex;
    if (index === siteData.mainPeriodIndex) {
      // If deleting the main period, set the first available period as main
      newMainIndex = updatedPeriods.length > 0 ? 0 : null;
    } else if (index < siteData.mainPeriodIndex) {
      // If deleting a period before the main period, adjust the index
      newMainIndex = siteData.mainPeriodIndex - 1;
    }
    
    setSiteData({
      ...siteData,
      constructionPeriods: updatedPeriods,
      mainPeriodIndex: newMainIndex
    });
    
    toast({
      title: "Period deleted",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };
  
  const handleMovePeriod = (index, direction) => {
    if (direction !== 'up' && direction !== 'down') return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === periodList.length - 1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updatedPeriods = [...periodList];
    const period = updatedPeriods[index];
    
    // Remove the period from its current position
    updatedPeriods.splice(index, 1);
    // Insert the period at its new position
    updatedPeriods.splice(newIndex, 0, period);
    
    // Adjust mainPeriodIndex if needed
    let newMainIndex = siteData.mainPeriodIndex;
    if (index === siteData.mainPeriodIndex) {
      // If moving the main period, update the index
      newMainIndex = newIndex;
    } else if (
      (direction === 'up' && index === siteData.mainPeriodIndex + 1) ||
      (direction === 'down' && index === siteData.mainPeriodIndex - 1)
    ) {
      // If moving a period across the main period, adjust the index
      newMainIndex = direction === 'up' ? siteData.mainPeriodIndex + 1 : siteData.mainPeriodIndex - 1;
    }
    
    setSiteData({
      ...siteData,
      constructionPeriods: updatedPeriods,
      mainPeriodIndex: newMainIndex
    });
  };
  
  const handleSetMainPeriod = (index) => {
    setSiteData({
      ...siteData,
      mainPeriodIndex: index
    });
    
    toast({
      title: "Main period updated",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };
  const handleEditPeriod = (index) => {
    setEditingPeriodIndex(index);
    onOpen(); // Open modal for editing
  };

  const handleOpenAddModal = () => {
    setEditingPeriodIndex(null); // Ensure we are in "add" mode
    onOpen(); // Open modal for adding
  };

  // Consolidate save logic for modal
  const handleSavePeriod = (periodData) => {
    if (editingPeriodIndex !== null) {
      handleUpdatePeriod(periodData);
    } else {
      handleAddPeriod(periodData);
    }
  };

  const handleDetailsFocus = (index) => {
    setActivePeriodIndexForCitation(index);
  };
  
  const registerPeriodDetailsRef = (index, ref) => {
    if (ref) {
      periodDetailsTextareaRefs.current[index] = ref;
    }
  };

  const handleInsertCitationInTimeline = (citationNumber) => {
    if (activePeriodIndexForCitation === null || activePeriodIndexForCitation === undefined) {
      toast({ title: "No period details field selected", description: "Click into a period's details field first.", status: "warning", duration: 3000 });
      return;
    }

    const periodIndex = activePeriodIndexForCitation;
    const activeTextarea = periodDetailsTextareaRefs.current[periodIndex];

    if (!activeTextarea) {
      toast({ title: "Text field not found", description: "Could not find the active text field to insert citation.", status: "error", duration: 3000 });
      return;
    }
    
    const { selectionStart, selectionEnd, value: currentDetails } = activeTextarea;
    
    const newDetails =
      currentDetails.substring(0, selectionStart) +
      `[${citationNumber}]` +
      currentDetails.substring(selectionEnd);
    
    const newCursorPosition = selectionStart + `[${citationNumber}]`.length;

    const updatedPeriods = periodList.map((p, idx) =>
      idx === periodIndex ? { ...p, details: newDetails } : p
    );

    setSiteData({
      ...siteData,
      constructionPeriods: updatedPeriods,
    });
    
    // Re-focus and set cursor position
    setTimeout(() => {
      if (periodDetailsTextareaRefs.current[periodIndex]) {
        const textAreaToFocus = periodDetailsTextareaRefs.current[periodIndex];
        textAreaToFocus.focus();
        textAreaToFocus.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  const allTimelineTextFields = useMemo(() => {
    const textFields = {};
    periodList.forEach((period, index) => {
      textFields[`period_${index}_details`] = period.details || '';
    });
    return textFields;
  }, [periodList.map(p => p.details).join('||')]); // Dependency based on content


  return (
    <Box display="flex" flexDirection="column" flexGrow={1} minH="0"> {/* Make root Box a flex container that grows */}
      <VStack spacing={6} align="stretch" minH="0" flexGrow={1} overflowY="auto" css={{ // Styling for a subtle scrollbar if it appears
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: useColorModeValue('gray.400', 'gray.500'),
          borderRadius: '24px',
        },
      }}>
        <Box minH="0"> {/* Ensure this box can shrink */}
          <Heading as="h1" size="lg" mb={2} fontFamily="heading">
            Construction Timeline
          </Heading>
          <Text color={mutedColor}>
            Add construction periods and important eras in the site's history
          </Text>
        </Box>
        
        {/* Timeline Help */}
        <Collapse in={showTimelineHelp} animateOpacity>
          <Box
            p={4}
            bg={useColorModeValue('yellow.50', 'rgba(214, 173, 96, 0.1)')}
            borderRadius="md"
            borderWidth="1px"
            borderColor={useColorModeValue('yellow.200', 'rgba(214, 173, 96, 0.3)')}
          >
            <VStack align="stretch" spacing={3}>
              <HStack>
                <WarningCircle size={20} color={accentColor} />
                <Text fontWeight="medium">Why Add Construction Periods?</Text>
              </HStack>
              
              <Text fontSize="sm">
                Many megalithic sites were built and modified over centuries or even millennia. Adding these construction periods helps:
              </Text>
              
              <VStack align="start" fontSize="sm" pl={4} spacing={2}>
                <HStack alignItems="flex-start" spacing={2}>
                  <Box pt={1}>•</Box>
                  <Text>Document different cultural influences throughout the site's history</Text>
                </HStack>
                <HStack alignItems="flex-start" spacing={2}>
                  <Box pt={1}>•</Box>
                  <Text>Record when specific features were added</Text>
                </HStack>
                <HStack alignItems="flex-start" spacing={2}>
                  <Box pt={1}>•</Box>
                  <Text>Track architectural changes over time</Text>
                </HStack>
              </VStack>
              
              <Button 
                size="sm" 
                onClick={() => setShowTimelineHelp(false)}
                alignSelf="flex-end"
                variant="ghost"
              >
                Got it
              </Button>
            </VStack>
          </Box>
        </Collapse>
        
        {/* Period List */}
        <Box minH="0"> {/* Added minH="0" to help with flex overflow */}
          <HStack justify="space-between" mb={4}>
            <Heading as="h2" size="md" fontFamily="heading">
              Construction Periods
            </Heading>
            <Button
              leftIcon={<PlusCircle />}
              colorScheme="yellow"
              size="sm"
              onClick={handleOpenAddModal} // Use new handler
              isDisabled={isOpen} // Disable if modal is already open
            >
              Add Period
            </Button>
          </HStack>

          {/* Visual Timeline */}
          {periodList.length > 0 && (
            <Box
              mb={6}
              p={4}
              bg={useColorModeValue('gray.50', 'gray.800')}
              borderRadius="md"
              position="relative"
              borderWidth="1px"
              borderColor={useColorModeValue('gray.200', 'gray.700')}
              maxH="700px" // Increased maximum height
              overflowY="auto" // Allow scrolling for this box if content exceeds maxHeight
              css={{ // Styling for a subtle scrollbar if it appears
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: useColorModeValue('gray.300', 'gray.600'),
                  borderRadius: '24px',
                },
              }}
            >
              <Text fontSize="sm" fontWeight="medium" mb={4}>
                Timeline Visualization
              </Text>
              
              <VisualTimeline periods={periodList} mainPeriodIndex={mainPeriodIndex} onClick={handleEditPeriod} />
            </Box>
          )}
          
          {/* Period Display */}
          <Box minH="0"> {/* Added minH="0" to help with flex overflow */}
            <AnimatePresence>
              {periodList.length > 0 ? (
                periodList.map((period, index) => (
                  <PeriodItem
                    key={`${index}-${period.periodName}`}
                    period={period}
                    index={index}
                    isMainPeriod={index === mainPeriodIndex}
                    onDelete={handleDeletePeriod}
                    onMoveUp={() => handleMovePeriod(index, 'up')}
                    onMoveDown={() => handleMovePeriod(index, 'down')}
                    onEdit={handleEditPeriod}
                    onSetAsMain={handleSetMainPeriod}
                    isFirst={index === 0}
                    isLast={index === periodList.length - 1}
                    totalCount={periodList.length}
                    siteData={siteData} // Pass siteData to PeriodItem
                  />
                ))
              ) : (
                <Box 
                  p={6} 
                  textAlign="center" 
                  borderWidth="1px" 
                  borderStyle="dashed" 
                  borderRadius="md"
                  borderColor={useColorModeValue('gray.200', 'gray.600')}
                >
                  <VStack spacing={3}>
                    <Clock size={24} opacity={0.5} />
                    <Text color={mutedColor}>
                      No construction periods added yet
                    </Text>
                    <Button
                      leftIcon={<PlusCircle />}
                      colorScheme="yellow"
                      size="sm"
                      onClick={handleOpenAddModal} // Use new handler
                    >
                      Add First Period
                    </Button>
                  </VStack>
                </Box>
              )}
            </AnimatePresence>
          </Box>
          
          {/* Help text for main period */}
          {periodList.length > 0 && (
            <Box mt={4} p={3} borderRadius="md" bg={cardBg}>
              <HStack>
                <Star size={16} color={accentColor} />
                <Text fontSize="sm" color={mutedColor}>
                  The main period is used for site overview information
                </Text>
              </HStack>
            </Box>
          )}
        </Box>
      </VStack>

      {/* Add/Edit Period Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingPeriodIndex !== null ? 'Edit Period' : 'Add New Period'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <PeriodForm
              period={editingPeriodIndex !== null ? periodList[editingPeriodIndex] : undefined}
              onSave={handleSavePeriod}
              onCancel={onClose} // Simple close for cancel
              isEditing={editingPeriodIndex !== null}
              onDetailsFocus={() => handleDetailsFocus(editingPeriodIndex)} // Pass focus handler
              periodIndex={editingPeriodIndex} // Pass index
              detailsInputRef={(el) => {
                if (el && editingPeriodIndex !== null) { // Ensure editingPeriodIndex is not null for ref assignment
                  periodDetailsTextareaRefs.current[editingPeriodIndex] = el;
                } else if (el && editingPeriodIndex === null) { // Handle ref for new period form if needed, though less critical
                  periodDetailsTextareaRefs.current['new'] = el; 
                }
              }}
              siteData={siteData} // Pass siteData to PeriodForm
            />
          </ModalBody>
          {/* Footer removed as buttons are now inside PeriodForm */}
        </ModalContent>
      </Modal>

      {/* Citation Manager Accordion */}
      <Box mt={8}>
        <Accordion allowToggle index={citationManagerAccordionIndex} onChange={(index) => setCitationManagerAccordionIndex(index)}>
          <AccordionItem borderWidth="1px" borderRadius="md">
            <h2>
              <AccordionButton py={3} _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }} aria-label="Manage Citations for Timeline Details" aria-controls="timeline-citation-panel" aria-expanded={citationManagerAccordionIndex === 0}>
                <HStack flex="1" textAlign="left">
                  <Notebook size={22} />
                  <Text fontWeight="medium">Manage Citations for Timeline</Text>
                  {(siteData.citations?.length > 0) && (<Badge borderRadius="full" px={2} colorScheme="blue">{siteData.citations.length}</Badge>)}
                </HStack>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4} id="timeline-citation-panel">
              <Text fontSize="sm" mb={3}>Manage citations that can be inserted into period details.</Text>
              <CitationManager
                textFields={allTimelineTextFields}
                // citations prop is no longer needed as CitationManager gets it from context
                onInsertCitation={handleInsertCitationInTimeline}
                // onCitationsChange is not needed as CitationManager uses context directly
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>

    </Box>
  );
};
